import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from '@/store';
import { userBrandsList } from '@/store/slices/casino-instance';

import { useReducedMotion } from '../../../../hooks/useReducedMotion';
import { AuroraField } from './AuroraField';
import { ScrollStrip } from './ScrollStrip';
import { StepNav } from './StepNav';
import { DeploymentSummaryModal } from './DeploymentSummaryModal';
import { LumioMorph } from './LumioMorph';
import { useDeploymentTimeline } from './useDeploymentSimulation';
import { brandDeploymentSteps, isBrandDeploying, useCurrentBrand } from './realDeployment';
import { statusFor } from './cardStatus';
import { useStripScroll } from './useStripScroll';
import { useStripChoreography } from './useStripChoreography';
import {
  Root,
  Glass,
  CompletedGlow,
  Header,
  TitleGroup,
  Title,
  ShinyTitle,
  HeaderMiddle,
  HeaderControls,
  CloseButton,
  ProgressTrack,
  ProgressFill,
  CollapseToggle,
} from './BrandDeploymentStrip.styles';

/** How often to re-fetch the brand list while a deploy is running (no live push exists). */
const DEPLOY_POLL_MS = 7000;

/** Chevron for the collapse / expand toggle — points UP (collapse) and flips to DOWN (expand)
 *  via a CSS rotate keyed on the button's `data-collapsed`. */
const Chevron = (): React.ReactElement => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M6 15l6-6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export type BrandDeploymentStripProps = {
  /** Dismisses the whole section (the "Close" affordance shown once complete). */
  onDismiss?: () => void;
  className?: string;
};

/**
 * Home "Brand Deployment" section — a fanned BOUNCE STACK of deploy-step cards headed by
 * the Lumio mark + title. It renders the selected brand's REAL content-deployment pipeline
 * (`displayBrand.status_details.steps`): settled history fanned behind, the active step in
 * progress at the front, pending steps ahead. The cards keep the RAW pipeline step names as
 * `label` and resolve their own friendly title + artwork + order via `resolveStepMeta`.
 *
 * The state is NOT simulated — it reflects the real snapshot, and since brand
 * content-deployment status is not pushed live, the section POLLS `userBrandsList()` while
 * deploying so the cards advance as the backend progresses. When the deploy finishes
 * (`status` leaves `content_deployment_in_progress`) it shows the "Completed" confirmation
 * card and stays until the owner closes it. The parent only mounts this while a deploy is
 * (or was) in progress, so an already-live brand never shows it.
 */
export const BrandDeploymentStrip = ({ onDismiss, className }: BrandDeploymentStripProps): React.ReactElement => {
  const { t } = useTranslation();
  const reduced = useReducedMotion();
  const dispatch = useAppDispatch();

  // The FRESH selected brand off the polled brand list (NOT the lagged `displayBrand`
  // snapshot, which `userBrandsList` never re-promotes), so the live status + steps
  // advance as the pipeline progresses. `useCurrentBrand` keeps identity stable across
  // identical polls, so the timeline (and its card ids) only churn on real changes.
  const brand = useCurrentBrand();
  const deploying = isBrandDeploying(brand);
  const brandName = brand?.casino_data?.details?.brand;
  const domain = brand?.casino_data?.details?.domain;

  const steps = React.useMemo(() => brandDeploymentSteps(brand), [brand]);
  const { timeline, cursor: liveCursor, done } = useDeploymentTimeline(steps, deploying);

  // While the pipeline runs, re-fetch the brand list so the cards advance as the real
  // backend progresses (content-deployment status has no live push). Stops the moment
  // the deploy leaves "in progress" (or the section unmounts).
  React.useEffect(() => {
    if (!deploying) return undefined;
    const id = window.setInterval(() => {
      dispatch(userBrandsList());
    }, DEPLOY_POLL_MS);
    return () => window.clearInterval(id);
  }, [deploying, dispatch]);

  // The strip renders the WHOLE timeline as one long, edge-to-edge horizontal row you
  // scroll through (completed on the left, the in-progress step focal, pending to the
  // right). While deploying, the confirmation (CTA) card is withheld — it only appears,
  // as the final card, once the run is done. Each card resolves its own live status by
  // its timeline index (statusFor), independent of where the viewer has scrolled.
  const total = timeline.length;
  const cards = React.useMemo(() => {
    const rendered = done ? timeline : timeline.slice(0, Math.max(0, total - 1));
    return rendered.map((entry, index) => ({ entry, status: statusFor(entry, index, liveCursor, done) }));
  }, [done, timeline, total, liveCursor]);
  // The card the strip auto-follows: the in-progress edge while deploying, the CTA once done.
  const focalIndex = done
    ? Math.max(0, cards.length - 1)
    : Math.min(Math.max(0, cards.length - 1), Math.max(0, liveCursor - 1));

  // Collapsed hides the card strip and shows a progress bar in the header instead; the strip's
  // scroll/choreography hooks are gated off while collapsed (their container is unmounted).
  // Starts collapsed — the compact progress bar is the default; expand to browse the cards.
  const [collapsed, setCollapsed] = React.useState(true);
  const toggleCollapsed = React.useCallback(() => setCollapsed((c) => !c), []);

  const stripRef = React.useRef<HTMLDivElement>(null);
  // useStripScroll auto-follows the live step + drives the chevrons; useStripChoreography
  // paints the per-frame scroll-linked card motion off the SAME container. Order matters:
  // useStripScroll runs its layout effect first (sets the initial auto-follow scrollLeft), so
  // the choreography's first paint reads the settled scroll position — no mount flash.
  const { atStart, atEnd, prev, next } = useStripScroll({
    containerRef: stripRef,
    focalIndex,
    count: cards.length,
    reduced,
    enabled: !collapsed,
  });
  useStripChoreography({ containerRef: stripRef, count: cards.length, reduced, enabled: !collapsed });

  // Collapsed-header progress: settled steps out of the real-step total (confirmation card
  // excluded); full once the deploy is done.
  const stepTotal = Math.max(0, total - 1);
  const completedCount = done ? stepTotal : Math.max(0, Math.min(stepTotal, liveCursor - 1));
  const progressPct = stepTotal > 0 ? (completedCount / stepTotal) * 100 : done ? 100 : 0;

  const [summaryOpen, setSummaryOpen] = React.useState(false);
  const onOpenSummary = React.useCallback(() => setSummaryOpen(true), []);
  const onCloseSummary = React.useCallback(() => setSummaryOpen(false), []);

  return (
    <Root className={className} aria-label={t('Brand Deployment')}>
      {/* While deploying: the live WebGL aurora behind frosted glass. Once COMPLETED:
          swap both for a soft STATIC green "success aurora" gradient. */}
      {done ? (
        <CompletedGlow aria-hidden="true" />
      ) : (
        <>
          <AuroraField reduced={reduced} />
          <Glass aria-hidden="true" />
        </>
      )}
      <Header>
        <LumioMorph enabled={!done} />
        <TitleGroup>
          {done ? (
            <Title text="Completed" tag="span" />
          ) : brandName ? (
            <ShinyTitle text="Crafting {{name}}" placeholders={{ name: brandName }} tag="span" />
          ) : (
            <ShinyTitle text="Crafting your website" tag="span" />
          )}
        </TitleGroup>
        <HeaderMiddle>
          {collapsed ? (
            <ProgressTrack
              role="progressbar"
              aria-label={t('Deployment progress')}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(progressPct)}
            >
              <ProgressFill $pct={progressPct} $active={!done} />
            </ProgressTrack>
          ) : null}
        </HeaderMiddle>
        <HeaderControls>
          {!collapsed && cards.length > 1 ? (
            <StepNav onPrev={prev} onNext={next} atStart={atStart} atEnd={atEnd} />
          ) : null}
          {done && onDismiss ? (
            <CloseButton type="button" onClick={onDismiss}>
              {t('Close')}
            </CloseButton>
          ) : null}
          <CollapseToggle
            type="button"
            data-collapsed={collapsed}
            aria-expanded={!collapsed}
            aria-label={collapsed ? t('Expand deployment') : t('Collapse deployment')}
            onClick={toggleCollapsed}
          >
            <Chevron />
          </CollapseToggle>
        </HeaderControls>
      </Header>

      {collapsed ? null : (
        <ScrollStrip
          ref={stripRef}
          cards={cards}
          reduced={reduced}
          brandName={brandName}
          domain={domain}
          onOpenSummary={onOpenSummary}
        />
      )}

      {summaryOpen ? <DeploymentSummaryModal timeline={timeline} domain={domain} onClose={onCloseSummary} /> : null}
    </Root>
  );
};

export default BrandDeploymentStrip;
